<template>
  <div id="boligvelgeren">
    <div id="scrollHelper" />
    <transition v-if="loadStatus === 'loaded'" name="fade" mode="out-in">
      <router-view />
    </transition>
    <transition v-else-if="loadStatus === 'empty' || loadStatus === 'error'" name="fade" mode="out-in">
      <error-message />
    </transition>
    <transition v-else name="fade" mode="out-in">
      <page-loader />
    </transition>
  </div>
</template>

<script>
// Vux ORM Models
import Availability from '@/models/Availability';
import Bathroom from '@/models/Bathroom';
import Bedroom from '@/models/Bedroom';
import Section from '@/models/Section';
import CalculatorSettings from '@/models/CalculatorSettings';
import Facility from '@/models/Facility';
import Level from '@/models/Level';
import Image from '@/models/Image';
import Illustration from '@/models/Illustration';
import Option from '@/models/Option';
import OptionCategory from '@/models/OptionCategory';
import Project from '@/models/Project';
import Residence from '@/models/Residence';
import ResidenceType from '@/models/ResidenceType';
import Selector from '@/models/Selector';
import Setting from '@/models/Setting';

// Components
import ErrorMessage from '@/components/ErrorMessage.vue';
import PageLoader from '@/components/PageLoader.vue';

export default {
  components: {
    ErrorMessage,
    PageLoader,
  },
  data() {
    return {
      baseURL: false,
      projectIds: [],
      hiddenId: false,
      hasSelector: false,
    };
  },
  computed: {
    loadStatus() {
      return this.$store.state.entities.residences.loadStatus;
    },
    selector() {
      return Selector.query()
        .with('projects')
        .first();
    },
  },
  watch: {
    selector() {
      this.hasSelector = true;
      this.projectIds = this.getProjectIds();
    },
    hasSelector() {
      const projectId = this.projectIds[0];
      this.getTaxonomies(projectId, this.baseURL);
      this.getIllustrations(projectId, this.baseURL);
      this.getResidences(projectId, this.baseURL, this.hiddenId);
      this.updateState();
    },
  },
  created() {
    this.setup();

    // Check if there is something in ?project= in URL and if so set in store
    if (this.$route.query.project) {
      this.updateProject(this.$route.query.project);
    }

    // Check if there is something in ?level= in URL and if so set in store
    if (this.$route.query.level) {
      this.updateLevel(this.$route.query.level);
    }

    if (this.$route.query.section) {
      this.updateSection(this.$route.query.section);
    }

    if (this.$route.query.availability) {
      this.updateAvailability(this.$route.query.availability);
    }

    if (this.$route.query.minPrice) {
      this.updateMinPrice(this.$route.query.minPrice);
    }

    if (this.$route.query.maxPrice) {
      this.updateMaxPrice(this.$route.query.maxPrice);
    }

    if (this.$route.query.minSize) {
      this.updateMinSize(this.$route.query.minSize);
    }

    if (this.$route.query.maxSize) {
      this.updateMaxSize(this.$route.query.maxSize);
    }

    if (this.$route.query.order) {
      this.updateOrder(this.$route.query.order);
    }

    if (this.$route.query.bedrooms) {
      this.updateBedrooms(this.$route.query.bedrooms);
    }

    if (this.$route.query.bathrooms) {
      this.updateBathrooms(this.$route.query.bathrooms);
    }

    if (this.$route.query.residenceType) {
      this.updateResidenceType(this.$route.query.residenceType);
    }


  },
  methods: {
    setup() {
      const settings = window.__selector_settings__;
      if (settings) {
        this.selectorId = settings.sourceData.selectorId;
        this.baseURL = settings.sourceData.sourceURL;
        this.hiddenId = settings.sourceData.hiddenId;

        // Import data
        this.getSelector(this.selectorId, this.baseURL);
      }
    },
    updateState() {
      const selector = this.selector;
      const cssVarsElement = document.querySelector('.s-selector-variables');
      if (selector) {
        // View settings
        Setting.commit(state => {
          state.colors = {
            loader: selector.colorLoader,
            buttons: selector.colorButtons,
            buttontext: selector.colorButtonText,
            forsale: selector.colorForSale,
            reserved: selector.colorReserved,
            sold: selector.colorSold,
            textcolor: selector.colorTextColor,
          };
        });

        if (selector.showColorOnResidenceForSale === true) {
          cssVarsElement.style.setProperty('--residence-opacity', '0.5');
        }

        if (selector.showColorOnSectionForSale === true) {
          cssVarsElement.style.setProperty('--section-opacity', '0.5');
        }

        // Check if selected color for the loader and update custom property.
        if (selector.colorLoader) {
          cssVarsElement.style.setProperty('--spinner-color', selector.colorLoader);
        }

        // Check if user selected color for buttons and update custom property.
        if (selector.colorButtons) {
          cssVarsElement.style.setProperty('--button-color', selector.colorButtons);
        }

        // Check if user selected text color for buttons and update custom property.
        if (selector.colorButtonText) {
          cssVarsElement.style.setProperty('--buttontext-color', selector.colorButtonText);
        }

        // Check if user selected color for "For sale" and update custom property.
        if (selector.colorForSale) {
          cssVarsElement.style.setProperty('--forsale-color', selector.colorForSale);
        }

        // Check if user selected color for "Reserved" and update custom property.
        if (selector.colorReserved) {
          cssVarsElement.style.setProperty('--reserved-color', selector.colorReserved);
        }

        // Check if user selected color for "Sold" and update custom property.
        if (selector.colorSold) {
          cssVarsElement.style.setProperty('--sold-color', selector.colorSold);
        }

        // Check if user selected text color and update custom property.
        if (selector.colorTextColor == true) {
          cssVarsElement.style.setProperty('--hover-textcolor', '#fff');
        }

        // Check if listType is not false and set predefined choice in state.
        if (selector.listType !== false) {
          Residence.commit(state => {
            state.listType = selector.listType;
          });
        }

        // Check if there is a set image size for card view thumbnails.
        if (selector.cardImageSize !== false) {
          Image.commit(state => {
            state.cardImageSize = selector.cardImageSize;
          });
        }

        // Check if there should be options and if so fetch data
        if (selector.hasOptions === true) {
          this.getOptionCategories(this.baseURL);
          this.getOptions(this.projectId, this.baseURL);
          Option.commit(state => {
            state.hasOptions = true;
          });
        }

        if (selector.hasCalculator === true) {
          CalculatorSettings.commit(state => {
            state.hasCalculator = true;
          });
          if (selector.hasComunalPricing === true) {
            CalculatorSettings.commit(state => {
              state.hasCommunalPrice = true;
            });
          }
          if (selector.interestRateIndividualSelect === true) {
            CalculatorSettings.commit(state => {
              state.hasMortgage = true;
            });
          }
          if (selector.taxDeductionCheck === true) {
            CalculatorSettings.commit(state => {
              state.hasTaxDeduction = true;
            });
          }
          if (localStorage.getItem('equity')) {
            CalculatorSettings.commit(state => {
              state.equity = parseFloat(localStorage.getItem('equity'));
            });
          }
          if (localStorage.getItem('mortgageInterestRate')) {
            CalculatorSettings.commit(state => {
              state.mortgageInterestRate = parseFloat(localStorage.getItem('mortgageInterestRate'));
            });
          }
          if (localStorage.getItem('mortgageYears')) {
            CalculatorSettings.commit(state => {
              state.mortgageYears = parseFloat(localStorage.getItem('mortgageYears'));
            });
          }
        }

        if (selector.hasContactForm === true) {
          Residence.commit(state => {
            state.hasContactForm = true;
          });
        }

        if (selector.showColorOnSectionForSale === true) {
          Residence.commit(state => {
            state.colorSection = true;
          });
        }

        if (selector.showColorOnResidenceForSale === true) {
          Residence.commit(state => {
            state.colorResidence = true;
          });
        }

        if (selector.colorReserved === true) {
          Residence.commit(state => {
            state.reservedPrice = true;
          });
        }

        if (selector.colorSold === true) {
          Residence.commit(state => {
            state.soldPrice = true;
          });
        }

      }
    },
    getTaxonomies(projectId, baseURL) {
      Availability.fetch(baseURL);
      Section.fetch(baseURL);
      Bathroom.fetch(baseURL);
      Bedroom.fetch(baseURL);
      Facility.fetch(baseURL);
      Level.fetch(baseURL);
      Project.fetch(baseURL, projectId);
      ResidenceType.fetch(baseURL);
    },
    getResidences(projectId, baseURL, exclude) {
      Residence.fetch(projectId, baseURL, exclude);
    },
    getSelector(selectorId, baseURL) {
      Selector.fetch(selectorId, baseURL);
    },
    getOptions(projectId, baseURL) {
      Option.fetch(projectId, baseURL);
    },
    getOptionCategories(baseURL) {
      OptionCategory.fetch(baseURL);
    },
    getIllustrations(projectId, baseURL) {
      Illustration.fetch(projectId, baseURL);
    },
    getProjectIds() {
      let ids = [];
      this.selector.projects.forEach(project => {
        ids.push(project.id);
      });
      return ids;
    },

    /* ======================================
     * Update State
     * ====================================== */
    updateProject(project) {
      Residence.commit(state => {
        state.selectedProject = project;
      });
    },
    updateLevel(level) {
      Residence.commit(state => {
        state.selectedLevel = level;
      });
    },
    updateSection(section) {
      Section.commit(state => {
        state.selectedSection = section;
      });
    },
    updateAvailability(availability) {
      Residence.commit(state => {
        state.residenceStatus = availability;
      });
    },
    updateMinPrice(minPrice) {
      Residence.commit(state => {
        state.minPrice = parseInt(minPrice);
      });
    },
    updateMaxPrice(maxPrice) {
      Residence.commit(state => {
        state.maxPrice = parseInt(maxPrice);
      });
    },
    updateMinSize(minSize) {
      Residence.commit(state => {
        state.minSize = parseInt(minSize);
      });
    },
    updateMaxSize(maxSize) {
      Residence.commit(state => {
        state.maxSize = parseInt(maxSize);
      });
    },
    updateOrder(order) {
      Residence.commit(state => {
        state.order = order;
      });
    },
    updateBedrooms(bedrooms) {
      Residence.commit(state => {
        state.bedrooms = bedrooms;
      });
    },
    updateBathrooms(bathrooms) {
      Residence.commit(state => {
        state.bathrooms = bathrooms;
      });
    },
    updateResidenceType(residenceType) {
      Residence.commit(state => {
        state.selectedResidenceType = residenceType;
      });
    },

  },
};
</script>
